import {
  internalProductCostOn,
  productMasterCostDefaults,
  productMasterRecordMap,
  rebateRateFor,
  resolvedProductCost,
  shippingProviderFor,
  JD_STORE_ID,
} from './productMaster'
import {
  computePinduoduoCashRecovery,
  jdCpsCouponDiscount,
  type AfterSalesInputs,
  type AfterSalesResult,
  type CalculatorDefaults,
  type CalculatorInputs,
  type CalculatorResult,
  type PinduoduoCashRecoveryResult,
} from './pinduoduoProfitCalculator'
import type { InternalProductInfoRecord, ProductMaster, ProductMasterRecord } from './types'

/**
 * 京东单件利润测算参数：沿用京东生产口径——
 * SKU级成本版本＞店铺结算/默认版本，快递按入仓/京东快递/圆通解析，
 * 配件与品牌特价无赠品无返点，返点＝产品成本×20%。
 * 单件计算复用通用公式 computePinduoduoUnitProfit（与生产逐日公式同构，
 * 由 verify-jd-profit-calculator.mjs 对真实SKU做parity校验）。
 */

const SHIPPING_PROVIDER_NAMES: Record<string, string> = {
  'jd-warehouse': '京东入仓',
  'jd-express': '京东快递',
  'yto-express': '圆通快递',
}

export interface JdCalculatorDefaults extends CalculatorDefaults {
  shippingProviderName: string
  fulfillmentModeLabel: string
}

export interface JdCashRecoveryAdvertisingInputs {
  // 直投费按每次下单发生；二退后重新销售会再次发生。
  directAdSpendPerOrdered: number
  // 京挑客退货退佣；每个最终清完的商品只扣一次成功成交佣金。
  cpsCommissionPerCompleted: number
}

/**
 * 京东库存现金回款只展示一条：按页面已填写值合计扣除直投费与京挑客佣金。
 * `inputs.price` 是当前商家实收口径；产品成本属于已投入的库存现金，
 * 不在本次回款里重复扣除。
 */
export function computeJdCashRecovery(
  result: CalculatorResult,
  inputs: CalculatorInputs,
  afterSales: AfterSalesResult,
  afterSalesInputs: AfterSalesInputs,
  advertising: JdCashRecoveryAdvertisingInputs,
  inventoryQuantity: number | null = null,
): PinduoduoCashRecoveryResult {
  const completionRate = 1 - Math.min(100, Math.max(0, afterSalesInputs.secondReturnRatePercent)) / 100
  const expectedAdvertisingSpendPerOrdered = advertising.directAdSpendPerOrdered
    + completionRate * advertising.cpsCommissionPerCompleted
  return computePinduoduoCashRecovery(
    result,
    inputs,
    afterSales,
    afterSalesInputs,
    expectedAdvertisingSpendPerOrdered,
    inventoryQuantity,
  )
}

const inferredAccessoryFromCompany = (records: InternalProductInfoRecord[]) => records.some((record) =>
  record.productName.includes('配件')
  || (record.ecommercePrice !== null && record.ecommercePrice > 0 && record.ecommercePrice < 30),
)

export const jdProductMasterContext = (productMaster: ProductMaster | null) => ({
  records: productMasterRecordMap(productMaster, JD_STORE_ID),
  defaults: productMasterCostDefaults(productMaster, JD_STORE_ID),
})

/**
 * 京东促销叠加：与活动分析页（D-115）同一价格链——
 * 默认按 PLUS会员折扣 → 店铺优惠券 → 跨店满减券 → 折扣活动 → 政府补贴 顺序折上折；
 * 计算器可传入拖动后的促销组：组间串行，组内所有促销共用进入该组时的价格并行计算；
 * 店铺优惠券为一次性满减：达到门槛减一次减额，未达门槛不生效（Gavin确认）；
 * 跨店满减券默认达到门槛后只减一次；可选“按每满比例分摊”，按 减额÷门槛 摊到本件（假设凑满）；
 * 用户到手价＝逐层折后价，商家实收＝到手价＋Σ(每层让利×平台承担比例)。
 */
export type JdPromotionBearer = 'merchant' | 'platform'
export type JdPromotionKey = 'plus' | 'coupon' | 'cross' | 'activity' | 'subsidy'
export type JdPromotionGroup = JdPromotionKey[]

export const DEFAULT_JD_PROMOTION_ORDER: JdPromotionKey[] = [
  'plus', 'coupon', 'cross', 'activity', 'subsidy',
]

export const normalizeJdPromotionOrder = (order: JdPromotionKey[] | undefined): JdPromotionKey[] => {
  const valid = new Set<JdPromotionKey>(DEFAULT_JD_PROMOTION_ORDER)
  const normalized = (order || []).filter((key, index, values) => valid.has(key) && values.indexOf(key) === index)
  return [...normalized, ...DEFAULT_JD_PROMOTION_ORDER.filter((key) => !normalized.includes(key))]
}

export const normalizeJdPromotionGroups = (
  groups: JdPromotionGroup[] | undefined,
  order?: JdPromotionKey[],
): JdPromotionGroup[] => {
  const fallbackOrder = normalizeJdPromotionOrder(order)
  if (!Array.isArray(groups) || groups.length === 0) return fallbackOrder.map((key) => [key])
  const valid = new Set<JdPromotionKey>(DEFAULT_JD_PROMOTION_ORDER)
  const seen = new Set<JdPromotionKey>()
  const normalized: JdPromotionGroup[] = []
  for (const group of groups) {
    if (!Array.isArray(group)) continue
    const members: JdPromotionKey[] = []
    for (const key of group) {
      if (!valid.has(key) || seen.has(key)) continue
      members.push(key)
      seen.add(key)
    }
    if (members.length) normalized.push(members)
  }
  for (const key of fallbackOrder) {
    if (!seen.has(key)) normalized.push([key])
  }
  return normalized
}

export const jdPromotionOrderFromGroups = (groups: JdPromotionGroup[]): JdPromotionKey[] => groups.flat()

export interface JdPromotionInputs {
  basePrice: number
  order?: JdPromotionKey[]
  groups?: JdPromotionGroup[]
  // 每种促销由勾选开关显式启用；未启用时该层完全不参与计算（缺省视为启用，兼容旧调用）
  activityEnabled?: boolean
  activityZhe: number
  activityBearer: JdPromotionBearer
  activityBearerSharePercent: number
  couponEnabled?: boolean
  couponThreshold: number
  couponAmount: number
  couponBearer: JdPromotionBearer
  crossEnabled?: boolean
  crossThreshold: number
  crossAmount: number
  crossProportional?: boolean
  crossBearer: JdPromotionBearer
  crossBearerSharePercent: number
  plusEnabled?: boolean
  plusZhe: number
  plusBearer: JdPromotionBearer
  plusBearerSharePercent: number
  subsidyEnabled?: boolean
  subsidyZhe: number
  subsidyBearer: JdPromotionBearer
}

export interface JdPromotionLayer {
  name: string
  discount: number
  merchantBorne: number
  platformBorne: number
  priceAfter: number
  groupBasePrice: number
  groupIndex: number
}

export interface JdPromotionResult {
  takeHomePrice: number
  merchantReceivable: number
  totalDiscount: number
  merchantBorneDiscount: number
  platformBorneDiscount: number
  layers: JdPromotionLayer[]
}

export interface JdCpsPromotionOutcome {
  takeHomePrice: number
  merchantReceivable: number
  totalDiscount: number
  merchantBorneDiscount: number
  platformBorneDiscount: number
  couponDiscount: number
}

// 京挑客专享券固定由商家承担，并且只叠加在基础促销之后的CPS订单上。
export function applyJdCpsCouponToPromotion(
  promotion: JdPromotionResult,
  couponAmount: number,
): JdCpsPromotionOutcome {
  const couponDiscount = jdCpsCouponDiscount(promotion.takeHomePrice, couponAmount)
  return {
    takeHomePrice: Math.max(promotion.takeHomePrice - couponDiscount, 0),
    merchantReceivable: Math.max(promotion.merchantReceivable - couponDiscount, 0),
    totalDiscount: promotion.totalDiscount + couponDiscount,
    merchantBorneDiscount: promotion.merchantBorneDiscount + couponDiscount,
    platformBorneDiscount: promotion.platformBorneDiscount,
    couponDiscount,
  }
}

// 默认勾选PLUS与店铺优惠券（店铺券默认满88减20），其余促销默认不参与（Gavin确认）
export function jdDefaultPromotionInputs(basePrice: number): JdPromotionInputs {
  return {
    basePrice,
    order: [...DEFAULT_JD_PROMOTION_ORDER],
    activityEnabled: false,
    activityZhe: 10,
    activityBearer: 'merchant',
    activityBearerSharePercent: 100,
    couponEnabled: true,
    couponThreshold: 88,
    couponAmount: 20,
    couponBearer: 'merchant',
    crossEnabled: false,
    crossThreshold: 0,
    crossAmount: 0,
    crossProportional: false,
    crossBearer: 'platform',
    crossBearerSharePercent: 100,
    plusEnabled: true,
    plusZhe: 9.5,
    plusBearer: 'merchant',
    plusBearerSharePercent: 100,
    subsidyEnabled: false,
    subsidyZhe: 8.5,
    subsidyBearer: 'platform',
  }
}

// 承担比例作用于所选承担方，其余部分由另一方承担；未提供比例时默认所选方全担
const bearerMerchantShareRatio = (bearer: JdPromotionBearer, sharePercent: number | undefined) => {
  const share = Math.min(Math.max(sharePercent ?? 100, 0), 100) / 100
  return bearer === 'merchant' ? share : 1 - share
}

export function computeJdPromotion(inputs: JdPromotionInputs): JdPromotionResult {
  const layers: JdPromotionLayer[] = []
  let price = Math.max(inputs.basePrice, 0)
  type PendingLayer = Pick<JdPromotionLayer, 'name' | 'discount'> & { merchantShareRatio: number }
  const layerCalculators: Record<JdPromotionKey, (groupBasePrice: number) => PendingLayer | null> = {
    plus: (groupBasePrice) => {
      if ((inputs.plusEnabled ?? true) && inputs.plusZhe > 0 && inputs.plusZhe < 10) {
        return {
          name: 'PLUS会员折扣',
          discount: groupBasePrice * (1 - inputs.plusZhe / 10),
          merchantShareRatio: bearerMerchantShareRatio(inputs.plusBearer, inputs.plusBearerSharePercent),
        }
      }
      return null
    },
    coupon: (groupBasePrice) => {
      // 店铺券为一次性满减：执行到该层时的价格达到门槛即减一次，未达门槛不生效
      if ((inputs.couponEnabled ?? true) && inputs.couponThreshold > 0 && inputs.couponAmount > 0 && groupBasePrice >= inputs.couponThreshold) {
        return {
          name: '店铺优惠券',
          discount: Math.min(inputs.couponAmount, groupBasePrice),
          merchantShareRatio: inputs.couponBearer === 'merchant' ? 1 : 0,
        }
      }
      return null
    },
    cross: (groupBasePrice) => {
      // 默认为达到门槛后只减一次；可选按每满比例摊到本件（假设跨店凑满）
      if ((inputs.crossEnabled ?? true) && inputs.crossThreshold > 0 && inputs.crossAmount > 0) {
        const discount = inputs.crossProportional
          ? groupBasePrice * Math.min(inputs.crossAmount / inputs.crossThreshold, 1)
          : groupBasePrice >= inputs.crossThreshold ? Math.min(inputs.crossAmount, groupBasePrice) : 0
        return discount > 0 ? {
          name: '跨店满减券',
          discount,
          merchantShareRatio: bearerMerchantShareRatio(inputs.crossBearer, inputs.crossBearerSharePercent),
        } : null
      }
      return null
    },
    activity: (groupBasePrice) => {
      if ((inputs.activityEnabled ?? true) && inputs.activityZhe > 0 && inputs.activityZhe < 10) {
        return {
          name: '折扣活动',
          discount: groupBasePrice * (1 - inputs.activityZhe / 10),
          merchantShareRatio: bearerMerchantShareRatio(inputs.activityBearer, inputs.activityBearerSharePercent),
        }
      }
      return null
    },
    subsidy: (groupBasePrice) => {
      if ((inputs.subsidyEnabled ?? true) && inputs.subsidyZhe > 0 && inputs.subsidyZhe < 10) {
        return {
          name: '政府补贴',
          discount: groupBasePrice * (1 - inputs.subsidyZhe / 10),
          merchantShareRatio: inputs.subsidyBearer === 'merchant' ? 1 : 0,
        }
      }
      return null
    },
  }
  for (const [groupIndex, group] of normalizeJdPromotionGroups(inputs.groups, inputs.order).entries()) {
    const groupBasePrice = price
    const pendingLayers = group
      .map((key) => layerCalculators[key](groupBasePrice))
      .filter((layer): layer is PendingLayer => layer !== null && layer.discount > 0)
    const requestedDiscount = pendingLayers.reduce((sum, layer) => sum + layer.discount, 0)
    const scale = requestedDiscount > groupBasePrice && requestedDiscount > 0
      ? groupBasePrice / requestedDiscount
      : 1
    const groupDiscount = requestedDiscount * scale
    price = Math.max(groupBasePrice - groupDiscount, 0)
    for (const pending of pendingLayers) {
      const discount = pending.discount * scale
      const merchantBorne = discount * pending.merchantShareRatio
      layers.push({
        name: pending.name,
        discount,
        merchantBorne,
        platformBorne: discount - merchantBorne,
        priceAfter: price,
        groupBasePrice,
        groupIndex,
      })
    }
  }
  const totalDiscount = layers.reduce((sum, layer) => sum + layer.discount, 0)
  const merchantBorneDiscount = layers.reduce((sum, layer) => sum + layer.merchantBorne, 0)
  const platformBorneDiscount = totalDiscount - merchantBorneDiscount
  return {
    takeHomePrice: price,
    merchantReceivable: price + platformBorneDiscount,
    totalDiscount,
    merchantBorneDiscount,
    platformBorneDiscount,
    layers,
  }
}

export function jdCalculatorDefaultsForSku(
  price: number,
  masterRecord: ProductMasterRecord | undefined,
  defaults: ReturnType<typeof jdProductMasterContext>['defaults'],
  companyRecords: InternalProductInfoRecord[],
  date: string,
): JdCalculatorDefaults {
  const productCost = internalProductCostOn(companyRecords, masterRecord, date)
  const isAccessory = masterRecord?.isAccessory ?? inferredAccessoryFromCompany(companyRecords)
  const giftExcluded = isAccessory || productCost.isBrandSpecial
  const shippingProvider = shippingProviderFor(
    masterRecord,
    masterRecord?.fulfillmentMode === 'jd-warehouse',
    date,
    defaults,
    isAccessory,
    productCost.isBrandSpecial,
  )
  const cost = resolvedProductCost(masterRecord, date, defaults, shippingProvider, giftExcluded)
  const rebateRate = rebateRateFor(isAccessory, productCost.isBrandSpecial, productCost.productCost)
  const inputs: CalculatorInputs = {
    price,
    productCost: productCost.productCost ?? 0,
    packagingCost: cost.packagingCost,
    giftCost: cost.giftCost,
    shippingInsuranceCost: cost.shippingInsuranceFee,
    shippingCost: cost.shippingFee,
    platformFeeRatePercent: cost.platformFeeRate,
    taxRatePercent: cost.taxRate,
    rebateRatePercent: rebateRate === null ? 0 : rebateRate * 100,
  }
  return {
    inputs,
    isAccessory,
    isBrandSpecial: productCost.isBrandSpecial,
    hasProductCost: productCost.productCost !== null,
    hasStoreCost: true,
    costIssue: productCost.issue,
    storeCostEffectiveDate: '',
    shippingProviderName: SHIPPING_PROVIDER_NAMES[cost.shippingProvider] || cost.shippingProvider,
    fulfillmentModeLabel: (masterRecord?.fulfillmentMode || 'merchant-direct') === 'jd-warehouse' ? '京东入仓' : '商家自发',
  }
}
